import type { ReactNode } from 'react'
import s from './Tree.module.scss'

export interface TreeNode {
  /** Eindeutig im ganzen Baum. Steht so in `expanded`. */
  id: string
  label: string
  /** Neben der Beschriftung, gedämpft — etwa „GICS 45". */
  hint?: ReactNode
  /** Rechtsbündig am Zeilenende — etwa die Anzahl. */
  value?: ReactNode
  /**
   * Kinder. **Fehlend und leer sind Verschiedenes:** `undefined` heißt „ein
   * Blatt", `[]` heißt „ein Knoten, dessen Kinder alle weggefiltert wurden".
   * Nur das erste bekommt kein Klappdreieck.
   */
  children?: TreeNode[]
  /** Rechts außen — ein Sprungziel, eine Aktion. Klickt nicht die Zeile mit. */
  action?: ReactNode
}

/**
 * Überschriften für die drei Spalten einer Zeile.
 *
 * Ohne sie steht der Baum ohne Kopf da, und die Zahl am Zeilenende ist eine
 * Zahl ohne Namen — man rät, ob sie Instrumente, Kinder oder Prozent meint.
 * Eine leere Angabe lässt die betreffende Spalte unbeschriftet.
 */
export interface TreeColumns {
  label?: ReactNode
  hint?: ReactNode
  value?: ReactNode
}

export interface TreeProps {
  nodes: TreeNode[]
  /** Die IDs der aufgeklappten Knoten. */
  expanded: string[]
  onToggle: (id: string) => void
  /** Klick auf die Zeile. Ohne diese Angabe ist die Zeile kein Ziel. */
  onSelect?: (node: TreeNode) => void
  /** Diese Zeile ist hervorgehoben. */
  selected?: string
  /** Überschriftenzeile. Ohne Angabe hat der Baum keinen Kopf. */
  columns?: TreeColumns
  'aria-label'?: string
}

/** Wie weit eine Ebene einrückt. Muss zur Breite des Dreiecks passen. */
const INDENT = 18

/**
 * Baum mit auf- und zuklappbaren Knoten.
 *
 * **Kontrolliert**, wie `Tabs` und `MultiSelect`: Welche Knoten offen sind,
 * weiß der Aufrufer. Nur so lässt sich der Zustand in die Adresse schreiben und
 * „alles zuklappen" von außen auslösen.
 *
 * Ein Knoten **ohne** Kinder bekommt kein Dreieck, aber dieselbe Einrückung wie
 * einer mit — sonst springt die Beschriftungsspalte von Zeile zu Zeile. Ein
 * Dreieck, das ins Leere führt, wäre schlimmer als keines: Man klickt, nichts
 * passiert, und man klickt noch einmal.
 *
 * Tastatur: Enter und Leertaste wählen, Pfeil rechts klappt auf, Pfeil links zu
 * — und auf einem schon geschlossenen Knoten führt Pfeil links zum Elternteil.
 * Bewusst **kein** Roving Tabindex über hoch und runter: Das wäre die volle
 * ARIA-Baumbedienung, und für eine Liste von elf Sektoren ist Tab genug.
 */
export function Tree({ nodes, expanded, onToggle, onSelect, selected, columns,
                       'aria-label': ariaLabel }: TreeProps) {
  const offen = new Set(expanded)

  // Hat der Baum Aktionen, braucht der Kopf rechts einen Platzhalter — sonst
  // endet die Ueberschrift „Titel" dort, wo in den Zeilen der Knopf sitzt, und
  // steht damit neben den Zahlen statt ueber ihnen. Gemessen an den Wurzeln:
  // Entweder tragen alle Knoten eine Aktion oder keiner.
  const mitAktion = nodes.some((node) => node.action)

  function zeile(node: TreeNode, level: number,
                 eltern: string | null): ReactNode[] {
    // `undefined` heißt Blatt, `[]` heißt „Kinder alle weggefiltert" — das
    // zweite behält sein Dreieck, sonst verschwände der Hinweis, dass hier
    // etwas fehlt.
    const hatKinder = node.children !== undefined
    const auf = hatKinder && offen.has(node.id)
    // Was ein Klick auf die ZEILE tut. Zwei Fälle, und die Reihenfolge ist
    // die Aussage:
    //
    //   mit `onSelect`  — auswählen. Wer eine Auswahl anbietet, meint sie:
    //                     "Aufklappen und Auswählen sind zwei Absichten"
    //                     (siehe der Klapper unten), und die Auswahl ist die
    //                     stärkere.
    //   ohne `onSelect` — auf- und zuklappen, sofern es Kinder gibt.
    //
    // Der zweite Fall war bisher NICHTS: Ein Klick auf den Namen verpuffte,
    // und nur das kleine Dreieck reagierte. Die TASTATUR konnte es längst —
    // Enter klappt in genau dieser Lage. Maus und Tastatur sagten also
    // Verschiedenes über dieselbe Zeile.
    const anKlick = onSelect
      ? () => onSelect(node)
      : (hatKinder ? () => onToggle(node.id) : undefined)

    const zeilen: ReactNode[] = [
      <div key={node.id} role="treeitem"
           aria-level={level}
           aria-expanded={hatKinder ? auf : undefined}
           aria-selected={selected === node.id ? true : undefined}
           className={`${s.zeile} ${selected === node.id ? s.gewaehlt : ''}`
                      + `${anKlick ? ` ${s.klickbar}` : ''}`}
           // `level - 1`: Die oberste Ebene beginnt am Rand. Mit `level`
           // stuende sie um eine Stufe eingerueckt, und der Kopf — der keine
           // Ebene hat — saesse daneben statt darueber.
           style={{ paddingLeft: `${(level - 1) * INDENT}px` }}
           tabIndex={0}
           onClick={anKlick}
           onKeyDown={(event) => {
             if (event.key === 'Enter' || event.key === ' ') {
               event.preventDefault()
               // `anKlick` deckt beide Faelle ab — auswaehlen oder klappen.
               // Enter tut damit genau dasselbe wie ein Mausklick.
               if (anKlick) anKlick()
             } else if (event.key === 'ArrowRight' && hatKinder && !auf) {
               event.preventDefault()
               onToggle(node.id)
             } else if (event.key === 'ArrowLeft') {
               event.preventDefault()
               if (auf) onToggle(node.id)
               // Schon zu: zum Elternteil. Das ist der Weg zurück nach oben,
               // ohne dass der Baum eine eigene Fokusverwaltung braucht.
               else if (eltern) {
                 const ziel = document.getElementById(`tree-${eltern}`)
                 ziel?.focus()
               }
             }
           }}
           id={`tree-${node.id}`}>
        {/*
          Der Klapper bleibt ein eigener Knopf INNERHALB der Zeile — auch
          jetzt, wo die ganze Zeile klappt. Er zeigt den Zustand an (▸ gegen
          ▾) und ist das gewohnte Ziel; ohne ihn sähe man einer Zeile nicht
          an, dass unter ihr etwas liegt.

          WO ES EINE AUSWAHL GIBT, ist er der einzige Weg zu klappen: Dort
          wählt die Zeile aus, und Aufklappen und Auswählen sind zwei
          Absichten — wer einen Sektor öffnet, meint nicht „filtere danach".
          Deshalb schluckt er den Klick.

          `aria-hidden`, weil `aria-expanded` schon an der Zeile steht — sonst
          liest der Screenreader den Zustand zweimal vor.
        */}
        {hatKinder ? (
          <button type="button" className={s.klapper} tabIndex={-1}
                  aria-hidden="true"
                  onClick={(event) => { event.stopPropagation(); onToggle(node.id) }}>
            {auf ? '▾' : '▸'}
          </button>
        ) : <span className={s.klapper} aria-hidden="true" />}

        <span className={s.text}>{node.label}</span>
        {node.hint !== undefined && node.hint !== null &&
          <span className={s.hinweis}>{node.hint}</span>}
        {node.value !== undefined && node.value !== null &&
          <span className={s.wert}>{node.value}</span>}
        {node.action &&
          // Die Aktion schluckt den Klick: Sonst löste sie zugleich die Zeile
          // aus, und man landete zweimal woanders.
          <span className={s.aktion}
                onClick={(event) => event.stopPropagation()}>{node.action}</span>}
      </div>,
    ]

    if (auf && node.children) {
      // `group` und nicht wieder `tree`: Ein Baum hat GENAU eine Wurzelrolle.
      // Verschachtelte `tree`-Rollen liest ein Screenreader als mehrere Bäume.
      zeilen.push(
        <div key={`${node.id}-kinder`} role="group">
          {node.children.map((kind) => zeile(kind, level + 1, node.id))}
        </div>)
    }
    return zeilen
  }

  return (
    <div className={s.rahmen}>
      {columns && (
        /*
          Die Kopfzeile steht AUSSERHALB von `role="tree"`. Ein Baum enthält
          Baumknoten und sonst nichts; eine Überschrift darin würde ein
          Screenreader als weiteren Knoten ansagen, den es nicht gibt.

          `aria-hidden`, weil sie damit ohnehin nicht mehr zum Baum gehört und
          eine vorgelesene Spaltenüberschrift ohne Zeilenbezug nichts nützt.
          Was die Zahl bedeutet, sagt das `aria-label` des Baums.

          Derselbe Aufbau wie eine Zeile, samt Platzhalter für den Klapper —
          sonst stünden die Überschriften um achtzehn Pixel versetzt über
          ihren Werten.
        */
        <div className={`${s.zeile} ${s.kopf}`} aria-hidden="true">
          <span className={s.klapper} />
          <span className={s.text}>{columns.label}</span>
          {columns.hint !== undefined &&
            <span className={s.hinweis}>{columns.hint}</span>}
          {columns.value !== undefined &&
            <span className={s.wert}>{columns.value}</span>}
          {mitAktion && <span className={s.aktion} />}
        </div>
      )}
      <div className={s.baum} role="tree" aria-label={ariaLabel}>
        {nodes.map((node) => zeile(node, 1, null))}
      </div>
    </div>
  )
}

/**
 * Baut aus einer FLACHEN Liste den Baum.
 *
 * Die Schnittstelle liefert flach, weil sich eine Liste sortieren und in eine
 * Tabelle kippen lässt und ein verschachteltes JSON nicht. Die Verschachtelung
 * entsteht hier, aus derselben Elternangabe, die auch in der Datenbank steht.
 *
 * Ein Eintrag, dessen Elternteil in der Liste FEHLT, wird zur Wurzel und
 * verschwindet nicht. Ein verwaister Verweis ist ein Mangel in den Daten; ihn
 * dadurch zu bestrafen, dass die Zeile aus der Anzeige fällt, machte aus einem
 * sichtbaren Mangel einen unsichtbaren.
 */
export function toTree<T>(
  items: T[],
  id: (item: T) => string,
  parent: (item: T) => string | null | undefined,
  node: (item: T, children: TreeNode[] | undefined) => TreeNode,
): TreeNode[] {
  const vorhanden = new Set(items.map(id))
  const kinder = new Map<string | null, T[]>()

  for (const item of items) {
    const eltern = parent(item)
    const schluessel = eltern && vorhanden.has(eltern) ? eltern : null
    const liste = kinder.get(schluessel)
    if (liste) liste.push(item)
    else kinder.set(schluessel, [item])
  }

  // `undefined` für Blätter, damit sie kein Dreieck bekommen — der Unterschied
  // zu einer leeren Liste ist hier die ganze Aussage.
  function bauen(item: T): TreeNode {
    const unten = kinder.get(id(item))
    return node(item, unten ? unten.map(bauen) : undefined)
  }

  return (kinder.get(null) ?? []).map(bauen)
}
